import React, { useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Paperclip } from 'lucide-react';
import { mediaUploadService, MediaFile } from '@/services/mediaUploadService';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface MediaInputProps {
  onMediaSelect: (files: MediaFile[]) => void;
  disabled?: boolean;
}

export function MediaInput({ onMediaSelect, disabled }: MediaInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { t } = useTranslation('conversations');

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const mediaFiles: MediaFile[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file type and size
      if (!mediaUploadService.validateFileType(file)) {
        toast({
          variant: "destructive",
          title: t('media.invalidFileType'),
          description: t('media.invalidFileTypeDesc', { filename: file.name })
        });
        continue;
      }

      if (!mediaUploadService.validateFileSize(file)) {
        toast({
          variant: "destructive",
          title: t('media.fileTooLarge'),
          description: t('media.fileTooLargeDesc', { filename: file.name })
        });
        continue;
      }
      
      const mediaFile = mediaUploadService.createMediaFile(file);
      mediaFiles.push(mediaFile);
    }
    
    if (mediaFiles.length > 0) {
      onMediaSelect(mediaFiles);
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onMediaSelect, toast, t]);

  const handlePaperclipClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <>
      {/* File input for media selection */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="*/*"
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={handlePaperclipClick}
        className="shrink-0"
      >
        <Paperclip className="h-4 w-4" />
      </Button>
    </>
  );
}